import Swiper from 'swiper';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';

class HeroOffer {
    constructor(el) {
        if (!this.set(el)) return;
        this.init();
    }

    set(el) {
        if (!el) return false;
        if (el.dataset.heroOfferInitialized === '1') return false;

        this.root = el;
        this.root.dataset.heroOfferInitialized = '1';
        this.swiperEl = this.root.querySelector('[data-hero-offer-swiper]');
        this.paginationEl = this.root.querySelector(
            '[data-hero-offer-pagination]'
        );
        this.labels = Array.from(
            this.swiperEl.querySelectorAll('[data-hero-offer-label]')
        ).map(el => el.getAttribute('data-hero-offer-label') || '');
        this.swiper = null;
        this.resizeTimer = null;

        return true;
    }

    init() {
        if (!this.swiperEl) return;

        const hasMultiple = this.swiperEl.querySelectorAll('.swiper-slide')
            .length > 1;

        this.applyMobileBackground();
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('load', () => this.setEqualHeights());
        this.bindImageLoad();

        this.swiper = new Swiper(this.swiperEl, {
            modules: [Autoplay, EffectFade, Pagination],
            init: false,
            speed: 800,
            loop: false,
            initialSlide: 0,
            rewind: hasMultiple,
            effect: 'fade',
            fadeEffect: {
                crossFade: true,
            },
            autoplay: hasMultiple
                ? {
                      delay: 5000,
                      disableOnInteraction: false,
                  }
                : false,
            pagination: {
                el: this.paginationEl,
                clickable: hasMultiple,
                renderBullet: (index, className) => {
                    const label = this.labels[index] || '';
                    return `<span class="hero-offer__dot flex flex-col gap-2 cursor-pointer pointer-events-auto" data-hero-offer-dot><span class="hero-offer__label block text-[13px]">${label}</span><span class="${className} hero-offer__bullet block w-[88px] h-px rounded-full bg-neutral-200 relative overflow-hidden opacity-100 cursor-pointer pointer-events-auto" data-hero-offer-bullet><span class="hero-offer__progress absolute left-0 top-0 h-full w-0 bg-black"></span></span></span>`;
                },
            },
        });

        if (this.swiper) {
            this.swiper.on('init', () => {
                this.updatePaginationState();
                this.resetProgress();
                this.setEqualHeights();
                setTimeout(() => this.setEqualHeights(), 0);
                this.bindPaginationClicks();
            });
            this.swiper.on('slideChange', () => {
                this.updatePaginationState();
                this.resetProgress();
            });
            this.swiper.init();
            this.bindPaginationClicks();
        }

        if (!hasMultiple && this.paginationEl) {
            this.paginationEl.style.display = 'none';
        }
    }

    applyMobileBackground() {
        if (!this.swiperEl) return;
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        const slides = this.swiperEl.querySelectorAll('[data-hero-offer-mobile-bg]');
        slides.forEach(slide => {
            const mobileBg = slide.getAttribute('data-hero-offer-mobile-bg');
            if (isMobile && mobileBg) {
                slide.style.backgroundImage = `url('${mobileBg}')`;
            }
        });
    }

    handleResize() {
        this.applyMobileBackground();
        if (this.resizeTimer) {
            window.clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = window.setTimeout(() => {
            this.setEqualHeights();
        }, 120);
    }

    bindImageLoad() {
        if (!this.swiperEl) return;
        const images = this.swiperEl.querySelectorAll('img');
        images.forEach(img => {
            if (img.complete) return;
            img.addEventListener(
                'load',
                () => {
                    this.setEqualHeights();
                },
                { once: true }
            );
        });
    }

    updatePaginationState() {
        if (!this.swiper) return;
        const activeSlide = this.swiper.slides[this.swiper.activeIndex];
        const theme =
            activeSlide?.getAttribute('data-hero-offer-pagination-theme') ||
            'light';
        if (this.root) {
            this.root.dataset.heroOfferPaginationTheme = theme;
        }
        if (!this.paginationEl) return;
        const dots = this.paginationEl.querySelectorAll(
            '[data-hero-offer-dot]'
        );
        dots.forEach((dot, index) => {
            if (index === this.swiper.realIndex) {
                dot.classList.add('hero-offer__dot--active');
            } else {
                dot.classList.remove('hero-offer__dot--active');
            }

            const label = dot.querySelector('.hero-offer__label');
            const bullet = dot.querySelector('.hero-offer__bullet');
            if (label) {
                label.classList.remove(
                    'text-white',
                    'text-white/70',
                    'text-neutral-400',
                    'text-neutral-900'
                );
                if (theme === 'light') {
                    label.classList.add(
                        index === this.swiper.realIndex
                            ? 'text-white'
                            : 'text-white/70'
                    );
                } else {
                    label.classList.add(
                        index === this.swiper.realIndex
                            ? 'text-neutral-900'
                            : 'text-neutral-400'
                    );
                }
            }

            if (bullet) {
                bullet.classList.remove('bg-white', 'bg-neutral-200');
                bullet.classList.add(
                    theme === 'light' ? 'bg-white' : 'bg-neutral-200'
                );
            }
        });
    }

    bindPaginationClicks() {
        if (!this.paginationEl || !this.swiper) return;
        const dots = this.paginationEl.querySelectorAll('[data-hero-offer-dot]');
        dots.forEach((dot, index) => {
            if (dot.dataset.heroOfferBound === '1') return;
            dot.dataset.heroOfferBound = '1';
            dot.addEventListener('click', event => {
                event.preventDefault();
                this.swiper.slideTo(index);
            });
        });
    }

    resetProgress() {
        if (!this.paginationEl) return;
        const progressEls =
            this.paginationEl.querySelectorAll('.hero-offer__progress');
        progressEls.forEach(el => {
            el.style.animation = 'none';
        });
        window.requestAnimationFrame(() => {
            progressEls.forEach(el => {
                el.style.animation = '';
            });
        });
    }

    setEqualHeights() {
        if (!this.swiperEl) return;
        const slides = Array.from(
            this.swiperEl.querySelectorAll('.swiper-slide')
        );
        if (!slides.length) return;
        let maxHeight = 0;
        this.swiperEl.style.height = 'auto';
        slides.forEach(slide => {
            slide.style.height = 'auto';
            const height = slide.scrollHeight;
            if (height > maxHeight) {
                maxHeight = height;
            }
        });
        if (maxHeight > 0) {
            this.swiperEl.style.height = `${maxHeight}px`;
            slides.forEach(slide => {
                slide.style.height = `${maxHeight}px`;
            });
        }
    }
}

const sections = document.querySelectorAll('[data-hero-offer]');
sections.forEach(el => new HeroOffer(el));
